import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronRight as Crumb, Phone, ArrowLeft, Home as HomeIcon, X } from 'lucide-react';
import { productsAPI } from '../services/api';
import { useLanguage, pickLang } from '../i18n/LanguageContext';
import './ProductDetailPage.css';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    loadProduct();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update SEO meta tags when product/lang changes
  useEffect(() => {
    if (!product) return;
    const title = pickLang(product, 'name', lang) || 'Product';
    const desc = pickLang(product, 'description', lang) || '';
    const keywords = lang === 'en'
      ? (product.seo_keywords_en || product.seo_keywords || '')
      : (product.seo_keywords || '');

    document.title = `${title} | QALS`;

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc.slice(0, 160));
    setMeta('keywords', keywords);

    return () => {
      document.title = 'QALS - Innovative Aluminum Systems';
    };
  }, [product, lang]);

  const loadProduct = async () => {
    try {
      const res = await productsAPI.getById(id);
      setProduct(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getAllImages = () => {
    if (!product) return [];
    const imgs = [];
    if (product.image_url) imgs.push(product.image_url);
    if (product.images && product.images.length > 0) {
      product.images.forEach(img => { if (!imgs.includes(img)) imgs.push(img); });
    }
    return imgs;
  };

  const images = getAllImages();

  const prev = () => setCurrentImage(c => (c === 0 ? images.length - 1 : c - 1));
  const next = () => setCurrentImage(c => (c === images.length - 1 ? 0 : c + 1));

  if (loading) {
    return (
      <div className="pdp-page">
        <div className="pdp-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pdp-page">
        <div className="pdp-not-found">
          <h2>404</h2>
          <button onClick={() => navigate('/')} className="pdp-btn-primary">
            <ArrowLeft size={16} /> {t('common.home')}
          </button>
        </div>
      </div>
    );
  }

  const category = product.category;
  const productName = pickLang(product, 'name', lang);
  const productDescription = pickLang(product, 'description', lang);
  const productTechnical = pickLang(product, 'technical_specs', lang);
  const categoryTitle = category ? t(`alum.cat.${category.id}`, category.title) : '';

  const getCertUrl = (path) => {
    if (!path) return '#';
    if (path.startsWith('http')) return path;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  };

  const tabs = [
    { id: 'description', label: t('pdp.tab.description'), short: t('pdp.tab.description.short') },
    { id: 'technical', label: t('pdp.tab.technical'), short: t('pdp.tab.technical.short') },
    { id: 'certifications', label: t('pdp.tab.certifications'), short: t('pdp.tab.certifications.short') },
  ];

  return (
    <div className="pdp-page" data-testid="product-detail-page">
      {/* Top bar */}
      <header className="pdp-topbar">
        <button className="pdp-back" onClick={() => navigate('/')} data-testid="pdp-home-btn">
          <ArrowLeft size={18} />
          <span>{t('common.home')}</span>
        </button>
        <div className="pdp-logo-wrap">
          <img
            src="https://customer-assets.emergentagent.com/job_bb24eabe-6ef2-4d89-a7b7-511a398af039/artifacts/wea2b3ng_1000023447.png"
            alt="QALS - Innovative Aluminum Systems"
            className="pdp-logo"
          />
        </div>
        <button className="pdp-lang" onClick={toggleLang} aria-label="Language" data-testid="pdp-lang-toggle">
          <span>{lang === 'el' ? '🇬🇧 EN' : '🇬🇷 EL'}</span>
        </button>
      </header>

      {/* Hero banner */}
      <div className="pdp-hero">
        <div className="pdp-hero__overlay" />
        <div className="pdp-hero__content">
          <h1>{productName}</h1>
          {category && <p className="pdp-hero__sub">{categoryTitle}</p>}
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="pdp-breadcrumb" aria-label="breadcrumb">
        <Link to="/"><HomeIcon size={14} /> {t('common.home')}</Link>
        <Crumb size={14} />
        <span>{t('pdp.products')}</span>
        {category && (
          <>
            <Crumb size={14} />
            <span>{categoryTitle}</span>
          </>
        )}
        <Crumb size={14} />
        <span className="pdp-breadcrumb__active">{productName}</span>
      </nav>

      {/* Main content */}
      <div className="pdp-container">
        <div className="pdp-grid">
          {/* Gallery */}
          <div className="pdp-gallery">
            <div className="pdp-gallery__main" onClick={() => images.length > 0 && setFullscreen(true)}>
              {images.length > 0 ? (
                <>
                  <img src={images[currentImage]} alt={productName} data-testid="pdp-main-image" />
                  {images.length > 1 && (
                    <>
                      <button
                        className="pdp-gallery__nav left"
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        aria-label="prev"
                      >
                        <ChevronLeft size={22} />
                      </button>
                      <button
                        className="pdp-gallery__nav right"
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        aria-label="next"
                      >
                        <ChevronRight size={22} />
                      </button>
                      <div className="pdp-gallery__counter">{currentImage + 1} / {images.length}</div>
                    </>
                  )}
                </>
              ) : (
                <div className="pdp-gallery__placeholder">—</div>
              )}
            </div>

            {images.length > 1 && (
              <div className="pdp-gallery__thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`pdp-thumb ${i === currentImage ? 'active' : ''}`}
                    onClick={() => setCurrentImage(i)}
                    aria-label={`img ${i + 1}`}
                  >
                    <img src={img} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="pdp-info">
            <div className="pdp-info__header">
              {product.code && <span className="pdp-info__code">{t('pdp.code')}: {product.code}</span>}
              <h2>{productName}</h2>
              {productDescription && (
                <p className="pdp-info__lead">{productDescription.split('\n')[0]}</p>
              )}
            </div>

            <div className="pdp-tabs">
              <div className="pdp-tabs__nav" role="tablist">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    role="tab"
                    className={`pdp-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`pdp-tab-${tab.id}`}
                  >
                    <span className="pdp-tab__label-full">{tab.label}</span>
                    <span className="pdp-tab__label-short">{tab.short}</span>
                  </button>
                ))}
              </div>

              <div className="pdp-tabs__panel">
                {activeTab === 'description' && (
                  <div className="pdp-tab-content">
                    {productDescription ? (
                      productDescription.split('\n').map((line, i) => (
                        line.trim() ? <p key={i}>{line}</p> : null
                      ))
                    ) : (
                      <p className="pdp-empty">{t('pdp.noDescription')}</p>
                    )}
                  </div>
                )}
                {activeTab === 'technical' && (
                  <div className="pdp-tab-content">
                    {productTechnical ? (
                      productTechnical.split('\n').map((line, i) =>
                        line.trim() ? <p key={i}>{line}</p> : null
                      )
                    ) : (
                      <p className="pdp-empty">{t('pdp.noTechnical')}</p>
                    )}
                  </div>
                )}
                {activeTab === 'certifications' && (
                  <div className="pdp-tab-content">
                    {product.certifications && product.certifications.length > 0 ? (
                      <ul className="pdp-cert-list">
                        {product.certifications.map(cert => (
                          <li key={cert.id} className="pdp-cert-item">
                            <div className="pdp-cert-item__info">
                              <span className="pdp-cert-icon" aria-hidden="true">📄</span>
                              <div>
                                <p className="pdp-cert-name">{cert.filename}</p>
                                <p className="pdp-cert-meta">
                                  {(cert.file_size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <a
                              href={getCertUrl(cert.file_path)}
                              download={cert.filename}
                              target="_blank"
                              rel="noreferrer"
                              className="pdp-cert-download"
                              data-testid={`pdp-cert-download-${cert.id}`}
                            >
                              Λήψη
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="pdp-empty">{t('pdp.noCerts')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="pdp-cta" data-testid="pdp-contact-cta">
          <div className="pdp-cta__inner">
            <div className="pdp-cta__icon">
              <Phone size={28} strokeWidth={1.5} />
            </div>
            <div className="pdp-cta__text">
              <h3>{t('pdp.cta.h3')}</h3>
              <p>{t('pdp.cta.p')}</p>
            </div>
            <a href="tel:+302108142770" className="pdp-cta__phone" data-testid="pdp-call-btn">
              <Phone size={20} />
              <span>210 814 2770</span>
            </a>
          </div>
        </div>
      </div>

      {/* Fullscreen viewer */}
      {fullscreen && images.length > 0 && (
        <div className="pdp-fullscreen" onClick={() => setFullscreen(false)}>
          <button className="pdp-fullscreen__close" onClick={() => setFullscreen(false)} aria-label="Κλείσιμο">
            <X size={28} />
          </button>
          <button
            className="pdp-fullscreen__nav left"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Προηγούμενη"
          >
            <ChevronLeft size={28} />
          </button>
          <img
            src={images[currentImage]}
            alt={productName}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="pdp-fullscreen__nav right"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Επόμενη"
          >
            <ChevronRight size={28} />
          </button>
          <div className="pdp-fullscreen__counter">{currentImage + 1} / {images.length}</div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
